import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getPacientesControllerListarQueryKey,
  usePacientesControllerAtualizar,
  usePacientesControllerCriar,
  usePacientesControllerEnviarFoto,
  usePacientesControllerRemoverFoto,
} from '@/api/generated/pacientes/pacientes';
import type { PacienteResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { ApiError } from '@/api/http-client';
import { ESPECIES, SEXOS } from '@/features/pacientes/lib/especies';
import { SeletorDeTutor, SEM_TUTOR } from '@/features/tutores/components/SeletorDeTutor';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { CampoDeFoto } from '@/shared/components/CampoDeFoto';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

const schema = z.object({
  nome: z.string().trim().min(1, 'Informe o nome do paciente').max(200),
  especie: z.enum(Object.keys(ESPECIES) as [keyof typeof ESPECIES]),
  sexo: z.enum(Object.keys(SEXOS) as [keyof typeof SEXOS]),
  raca: z.string().trim().max(120).or(z.literal('')),
  dataNascimento: z.string().or(z.literal('')),
  // Vem de `<input type="number">`, que entrega string. O `''` é campo vazio,
  // não zero — por isso o coerce só acontece depois do filtro no envio.
  pesoKg: z.string().or(z.literal('')),
  microchip: z.string().trim().max(40).or(z.literal('')),
  castrado: z.enum(['sim', 'nao']),
  observacoes: z.string().trim().max(2000).or(z.literal('')),
  tutorId: z.string(),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  paciente?: PacienteResponse;
}

/**
 * Cadastro e edição de paciente (F5-1).
 *
 * ⚠️ **O veterinário responsável não é campo desta tela.** O servidor assume
 * quem está criando quando o DTO não manda `veterinarioId`, e o schema proíbe
 * paciente órfão — "não existe mais paciente sem dono como na V1". Escolher o
 * responsável é transferência de carteira, que é outra operação.
 */
export function PacienteFormDialogo({ aberto, aoFechar, paciente }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(paciente);

  const criar = usePacientesControllerCriar();
  const atualizar = usePacientesControllerAtualizar();
  const enviarFoto = usePacientesControllerEnviarFoto();
  const removerFoto = usePacientesControllerRemoverFoto();

  // A foto é gravada por rota PRÓPRIA (`POST /pacientes/:id/foto`), fora do
  // corpo do formulário — por isso o estado dela vive aqui e não no
  // react-hook-form. `fotoUrl` acompanha a resposta do servidor a cada troca:
  // reler da prop `paciente` mostraria a foto antiga até a lista revalidar.
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  // Só no cadastro: sem id, não há onde subir a imagem antes de salvar.
  const [fotoPendente, setFotoPendente] = useState<{ arquivo: File; url: string } | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;

    setFotoUrl(paciente?.fotoUrl ?? null);
    setFotoPendente(null);

    reset({
      nome: paciente?.nome ?? '',
      especie: paciente?.especie ?? 'CANINO',
      sexo: paciente?.sexo ?? 'INDEFINIDO',
      raca: paciente?.raca ?? '',
      // `<input type="date">` só aceita `yyyy-MM-dd`; a API devolve ISO cheio.
      dataNascimento: paciente?.dataNascimento?.slice(0, 10) ?? '',
      pesoKg: paciente?.pesoKg != null ? String(paciente.pesoKg) : '',
      microchip: paciente?.microchip ?? '',
      castrado: paciente?.castrado ? 'sim' : 'nao',
      observacoes: paciente?.observacoes ?? '',
      tutorId: paciente?.tutorId ?? SEM_TUTOR,
    });
  }, [aberto, paciente, reset]);

  // A prévia local é um `blob:` que segura o arquivo em memória até ser
  // revogado. Sem isto, trocar de foto cinco vezes com o diálogo aberto deixa
  // cinco imagens presas até a aba fechar.
  useEffect(() => {
    const url = fotoPendente?.url;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [fotoPendente]);

  /**
   * No paciente que já existe, a foto sobe NA HORA — não espera o "Salvar".
   *
   * É rota separada, e fingir que faz parte do formulário criaria o pior dos
   * casos: quem trocasse a foto e cancelasse o diálogo veria a imagem antiga de
   * volta na lista, sem nada explicando por quê.
   */
  async function trocarFoto(arquivo: File) {
    if (!paciente) {
      setFotoPendente({ arquivo, url: URL.createObjectURL(arquivo) });
      return;
    }

    try {
      const atualizado = await enviarFoto.mutateAsync({ id: paciente.id, data: { arquivo } });
      setFotoUrl(atualizado.fotoUrl);
      await queryClient.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() });
      toast.success('Foto atualizada.');
    } catch (erro) {
      // A mensagem do servidor cita a regra que reprovou ("a imagem tem 120px
      // de largura; o mínimo é 200"). Trocá-la por um texto genérico aqui
      // esconderia exatamente o que a pessoa precisa saber para acertar.
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível enviar a foto.');
    }
  }

  async function tirarFoto() {
    if (!paciente) {
      setFotoPendente(null);
      return;
    }

    try {
      const atualizado = await removerFoto.mutateAsync({ id: paciente.id });
      setFotoUrl(atualizado.fotoUrl);
      await queryClient.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() });
      toast.success('Foto removida.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível remover a foto.');
    }
  }

  const aoEnviar = handleSubmit(async (dados) => {
    // Campo vazio vira `null` e não string vazia: o `.nullable()` do DTO existe
    // para "não informado", e `''` gravaria um valor em branco no lugar.
    const corpo = {
      nome: dados.nome,
      especie: dados.especie,
      sexo: dados.sexo,
      raca: dados.raca.trim() || null,
      dataNascimento: dados.dataNascimento || null,
      pesoKg: dados.pesoKg.trim() ? Number(dados.pesoKg) : null,
      microchip: dados.microchip.trim() || null,
      castrado: dados.castrado === 'sim',
      observacoes: dados.observacoes.trim() || null,
      tutorId: dados.tutorId || null,
    };

    try {
      if (paciente) {
        await atualizar.mutateAsync({ id: paciente.id, data: corpo });
      } else {
        const criado = await criar.mutateAsync({ data: corpo });

        // A foto só pode subir DEPOIS: a rota é `POST /pacientes/:id/foto`, e o
        // id só existe agora.
        //
        // ⚠️ Falha aqui não desfaz o cadastro nem reabre o formulário. O
        // paciente foi criado e é o dado que importa; um `throw` levaria o
        // fluxo para o `catch` de baixo e diria "não foi possível salvar o
        // paciente", que seria mentira — e a pessoa cadastraria o animal de
        // novo, agora em duplicata.
        if (fotoPendente) {
          try {
            await enviarFoto.mutateAsync({ id: criado.id, data: { arquivo: fotoPendente.arquivo } });
          } catch (erro) {
            toast.warning(
              `Paciente cadastrado, mas a foto não entrou: ${
                erro instanceof ApiError ? erro.message : 'falha no envio'
              } Envie de novo pela edição.`,
            );
          }
        }
      }

      await queryClient.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() });
      toast.success(editando ? 'Paciente atualizado.' : 'Paciente cadastrado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o paciente.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editando ? 'Informações complementares do paciente' : 'Novo paciente'}
          </DialogTitle>
          <DialogDescription>
            {editando
              ? 'Dados completos permitem à Nohra conectar análises e oferecer mais contexto clínico para o acompanhamento do paciente.'
              : 'Espécie, sexo e nascimento definem a faixa de referência usada na leitura dos exames.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col items-center gap-1.5 pb-2">
            <CampoDeFoto
              nome={watch('nome') || paciente?.nome}
              urlAtual={fotoPendente ? fotoPendente.url : fotoUrl}
              enviando={enviarFoto.isPending || removerFoto.isPending}
              aoEnviar={(arquivo) => void trocarFoto(arquivo)}
              aoRemover={() => void tirarFoto()}
            />
            {/* Sem paciente ainda, não há onde subir a foto de verdade — a
                rota é `POST /pacientes/:id/foto`, e o id só existe depois do
                "Cadastrar". Sem este aviso, a prévia local pareceria já salva. */}
            {fotoPendente && <p className="text-xs text-muted-foreground">Será enviada ao salvar.</p>}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              id="nome"
              rotulo="Nome"
              erro={errors.nome?.message}
              obrigatorio
              className="sm:col-span-2"
            >
              <Input
                id="nome"
                className={CLASSE_CONTROLE}
                placeholder="Ex.: Amora"
                {...register('nome')}
              />
            </Campo>

            <Campo id="especie" rotulo="Espécie" erro={errors.especie?.message} obrigatorio>
              <Controller
                control={control}
                name="especie"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="especie" className={CLASSE_CONTROLE}>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ESPECIES).map(([valor, rotulo]) => (
                        <SelectItem key={valor} value={valor}>
                          {rotulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Campo>

            <Campo
              id="sexo"
              rotulo="Sexo"
              erro={errors.sexo?.message}
              dica="Algumas faixas de referência mudam com o sexo."
            >
              <Controller
                control={control}
                name="sexo"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="sexo" className={CLASSE_CONTROLE}>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SEXOS).map(([valor, rotulo]) => (
                        <SelectItem key={valor} value={valor}>
                          {rotulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Campo>

            <div className="sm:col-span-2 grid grid-cols-3 gap-4">
              <Campo id="raca" rotulo="Raça" erro={errors.raca?.message}>
                <Input
                  id="raca"
                  className={CLASSE_CONTROLE}
                  placeholder="Ex.: SRD"
                  {...register('raca')}
                />
              </Campo>

              <Campo
                id="dataNascimento"
                rotulo="Nascimento"
                erro={errors.dataNascimento?.message}
                dica="Sem esta data, faixas por idade não se aplicam."
              >
                <Input
                  id="dataNascimento"
                  type="date"
                  className={CLASSE_CONTROLE}
                  {...register('dataNascimento')}
                />
              </Campo>

              <div className="space-y-2">
                <Label className="invisible" aria-hidden="true">
                  Castrado
                </Label>
                <Controller
                  control={control}
                  name="castrado"
                  render={({ field }) => (
                    <div className="flex items-center gap-4 h-10">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="castrado-sim"
                          checked={field.value === 'sim'}
                          onCheckedChange={(marcado) => marcado && field.onChange('sim')}
                        />
                        <Label htmlFor="castrado-sim" className="font-normal cursor-pointer">
                          Castrado
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="castrado-nao"
                          checked={field.value === 'nao'}
                          onCheckedChange={(marcado) => marcado && field.onChange('nao')}
                        />
                        <Label htmlFor="castrado-nao" className="font-normal cursor-pointer">
                          Não castrado
                        </Label>
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            <Campo id="pesoKg" rotulo="Peso (kg)" erro={errors.pesoKg?.message}>
              <Input
                id="pesoKg"
                type="number"
                step="0.001"
                min="0"
                className={CLASSE_CONTROLE}
                placeholder="Ex.: 8,4"
                {...register('pesoKg')}
              />
            </Campo>

            <Campo id="microchip" rotulo="Microchip" erro={errors.microchip?.message}>
              <Input id="microchip" className={CLASSE_CONTROLE} {...register('microchip')} />
            </Campo>

            <Campo
              id="tutorId"
              rotulo="Responsável"
              erro={errors.tutorId?.message}
              dica="Busque por nome, documento, e-mail ou telefone. Não achou? Cadastre no +."
              className="sm:col-span-2"
            >
              <Controller
                control={control}
                name="tutorId"
                render={({ field }) => (
                  <SeletorDeTutor
                    id="tutorId"
                    valor={field.value}
                    aoEscolher={field.onChange}
                    nomeAtual={paciente?.tutorNome ?? null}
                    tutorVinculadoId={paciente?.tutorId ?? null}
                    habilitado={aberto}
                  />
                )}
              />
            </Campo>

            <Campo
              id="observacoes"
              rotulo="Observações"
              erro={errors.observacoes?.message}
              className="sm:col-span-2"
            >
              <Textarea
                id="observacoes"
                rows={3}
                className={CLASSE_CONTROLE}
                placeholder="Alergias, comorbidades, temperamento..."
                {...register('observacoes')}
              />
            </Campo>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Cadastrar paciente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
